import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  MessageSquare, Search, X, Loader2, RefreshCw,
  Phone, MapPin, Calendar, User, CheckCircle2,
  Clock, Zap, Star, Inbox, XCircle, Plus, ChevronRight
} from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import { useAuthStore } from '@/store/authStore'
import { ROUTES } from '@/constants'
import type { Task, TaskStatus } from '@/types'
import toast from 'react-hot-toast'

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CFG: Record<TaskStatus, { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string }> = {
  open:             { label: 'Open',             bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     icon: <Inbox size={10} />,        dot: '#0ea5e9' },
  assigned:         { label: 'Assigned',         bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    icon: <User size={10} />,         dot: '#3b82f6' },
  in_progress:      { label: 'In Progress',      bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  icon: <Zap size={10} />,          dot: '#8b5cf6' },
  site_visit:       { label: 'Site Visit',       bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  icon: <MapPin size={10} />,       dot: '#6366f1' },
  work_in_progress: { label: 'Work In Progress', bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  icon: <Zap size={10} />,          dot: '#f97316' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   icon: <Clock size={10} />,        dot: '#f59e0b' },
  pending_feedback: { label: 'Pending Feedback', bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  icon: <Star size={10} />,         dot: '#a855f7' },
  closed:           { label: 'Closed',           bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={10} />, dot: '#10b981' },
  cancelled:        { label: 'Cancelled',        bg: 'bg-gray-50',    text: 'text-gray-500',    border: 'border-gray-200',    icon: <XCircle size={10} />,      dot: '#9ca3af' },
}

const STATUS_THEME: Record<TaskStatus, { color: string; shadow: string }> = {
  open:             { color: '#0ea5e9', shadow: 'rgba(14,165,233,0.08)' },
  assigned:         { color: '#3b82f6', shadow: 'rgba(59,130,246,0.08)' },
  in_progress:      { color: '#8b5cf6', shadow: 'rgba(139,92,246,0.08)' },
  site_visit:       { color: '#6366f1', shadow: 'rgba(99,102,241,0.08)' },
  work_in_progress: { color: '#f97316', shadow: 'rgba(249,115,22,0.08)' },
  pending_approval: { color: '#f59e0b', shadow: 'rgba(245,158,11,0.15)' },
  pending_feedback: { color: '#a855f7', shadow: 'rgba(168,85,247,0.08)' },
  closed:           { color: '#10b981', shadow: 'rgba(16,185,129,0.06)' },
  cancelled:        { color: '#9ca3af', shadow: 'rgba(156,163,175,0.04)' },
}

const PRIORITY_CFG: Record<string, { bar: string; badge: string; text: string }> = {
  low:    { bar: '#94a3b8', badge: 'bg-gray-100',   text: 'text-gray-500'   },
  medium: { bar: '#8b5cf6', badge: 'bg-violet-100', text: 'text-violet-700' },
  high:   { bar: '#f97316', badge: 'bg-orange-100', text: 'text-orange-700' },
  urgent: { bar: '#ef4444', badge: 'bg-red-100',    text: 'text-red-700'    },
}

const STAGE_FILTERS: { label: string; value: TaskStatus | 'all' }[] = [
  { label: 'All',      value: 'all'              },
  { label: 'Open',     value: 'open'             },
  { label: 'Assigned', value: 'assigned'         },
  { label: 'Active',   value: 'in_progress'      },
  { label: 'Approval', value: 'pending_approval' },
  { label: 'Closed',   value: 'closed'           },
]

// ── Enquiry card ──────────────────────────────────────────────────────────────
const EnquiryCard = ({ task, onClick }: { task: Task; onClick: () => void }) => {
  const s = STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.medium
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  const theme = STATUS_THEME[task.status]

  return (
    <div onClick={onClick}
      className="group bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-0.5 border-l-[4px]"
      style={{
        boxShadow: `0 2px 12px ${theme.shadow}, 0 0 0 1px rgba(0,0,0,0.04)`,
        borderLeftColor: theme.color
      }}>
      <div className="h-1 w-full" style={{ background: p.bar }} />
      <div className="p-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center text-sm font-black text-white shrink-0"
              style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }}>
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-black text-gray-900 truncate">{task.customerName}</p>
              <p className="text-[10px] text-gray-400 font-mono">{task.taskNumber}</p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full border shrink-0 ${s.bg} ${s.text} ${s.border}`}>
            {s.icon} {s.label}
          </span>
        </div>

        {/* Details */}
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Phone size={10} className="text-gray-400 shrink-0" />
            <span>+91 {task.customerPhone}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 truncate">
            <MapPin size={10} className="text-gray-400 shrink-0" />
            <span className="truncate">{task.address}{task.city ? `, ${task.city}` : ''}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar size={10} className="text-gray-400 shrink-0" />
            <span>{task.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
          {task.assignedEngineerName && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <User size={10} className="text-gray-400 shrink-0" />
              <span>{task.assignedEngineerName}</span>
            </div>
          )}
          {task.description && (
            <p className="text-xs text-gray-400 italic line-clamp-2 mt-1">"{task.description}"</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${p.badge} ${p.text} capitalize`}>
            {task.priority}
          </span>
          <ChevronRight size={13} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
const InquiryListPage = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = user?.role === 'admin'

  const [tasks, setTasks]     = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [filter, setFilter]   = useState<TaskStatus | 'all'>(
    location.state?.status || 'all'
  )

  useEffect(() => {
    if (location.state?.status) {
      setFilter(location.state.status)
    }
  }, [location.state?.status])

  const load = useCallback(async () => {
    setLoading(true)
    const res = await taskService.list()
    if (res.success && res.data) {
      const myIds = [user?.uid, user?.id, user?.engineerDocId].filter(Boolean) as string[]
      setTasks(res.data.filter(t => 
        t.type === 'enquiry' && 
        (isAdmin || !t.assignedTo || myIds.includes(t.assignedTo))
      ))
    } else {
      toast.error('Failed to load enquiries')
    }
    setLoading(false)
  }, [isAdmin, user?.uid, user?.id, user?.engineerDocId])

  useEffect(() => { load() }, [load])

  const filtered = tasks.filter(t => {
    const matchFilter = filter === 'all' || t.status === filter
    const q = search.toLowerCase()
    const matchSearch = !q || [t.customerName, t.customerPhone, t.taskNumber, t.address, t.city, t.assignedEngineerName, t.description]
      .some(v => v?.toLowerCase().includes(q))
    return matchFilter && matchSearch
  })

  const total   = tasks.length
  const open    = tasks.filter(t => t.status === 'open').length
  const active  = tasks.filter(t => ['assigned','in_progress','site_visit','work_in_progress'].includes(t.status)).length
  const closed  = tasks.filter(t => t.status === 'closed').length

  return (
    <div className="space-y-5 pb-10">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 50%,#0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(139,92,246,0.35) 0%,transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-1/3 w-40 h-28 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(99,102,241,0.2) 0%,transparent 70%)', filter: 'blur(30px)' }} />
        </div>
        <div className="relative z-10 p-5 lg:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-3">
              <MessageSquare size={11} className="text-violet-300" />
              <span className="text-[11px] text-blue-200 font-semibold uppercase tracking-wider">Enquiry Tasks</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-black">Enquiries</h1>
            <p className="text-blue-300/70 text-sm mt-1">{total} enquir{total !== 1 ? 'ies' : 'y'} · {active} active</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'enquiry' } })}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all hover:scale-[1.03]"
                style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 16px rgba(139,92,246,0.3)' }}>
                <Plus size={15} /> New Enquiry
              </button>
            )}
            <button onClick={load}
              className="p-2.5 bg-white/10 rounded-xl hover:bg-white/20 transition-all" title="Refresh">
              <RefreshCw size={15} className={`text-blue-200 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total',  value: total,  color: 'text-gray-800',    bg: 'bg-white'       },
          { label: 'Open',   value: open,   color: 'text-sky-700',     bg: 'bg-sky-50'      },
          { label: 'Active', value: active, color: 'text-violet-700',  bg: 'bg-violet-50'   },
          { label: 'Closed', value: closed, color: 'text-emerald-700', bg: 'bg-emerald-50'  },
        ].map(m => (
          <div key={m.label} className={`${m.bg} rounded-2xl px-3 py-3 text-center`}
            style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
            <p className={`text-xl font-black ${m.color}`}>{m.value}</p>
            <p className="text-[9px] font-bold text-gray-400 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      {/* ── Search ── */}
      <div className="relative">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search customer, phone, task ID, description…"
          className="w-full pl-9 pr-9 py-2.5 text-sm bg-white border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all text-gray-700" />
        {search && (
          <button onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={13} />
          </button>
        )}
      </div>

      {/* ── Status filter pills ── */}
      <div className="flex gap-2 flex-wrap">
        {STAGE_FILTERS.map(f => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            className={`text-[11px] font-black px-3 py-1.5 rounded-xl transition-all ${
              filter === f.value
                ? 'bg-violet-600 text-white shadow-sm'
                : 'bg-white text-gray-500 hover:text-gray-800 border border-gray-200'
            }`}>
            {f.label}
            {f.value !== 'all' && tasks.filter(t => t.status === f.value).length > 0 && (
              <span className={`ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full ${filter === f.value ? 'bg-white/20' : 'bg-gray-100'}`}>
                {tasks.filter(t => t.status === f.value).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-14 h-14 rounded-3xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.1),rgba(99,102,241,0.08))' }}>
            <Loader2 size={22} className="text-violet-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading enquiries…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-16 h-16 rounded-3xl flex items-center justify-center bg-gray-50">
            <MessageSquare size={24} className="text-gray-300" />
          </div>
          <p className="text-sm font-bold text-gray-500">No enquiries found</p>
          <p className="text-xs text-gray-400">
            {search ? 'Try a different search term' : filter !== 'all' ? 'No enquiries in this status' : 'Create a new enquiry task to get started'}
          </p>
          {isAdmin && !search && filter === 'all' && (
            <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'enquiry' } })}
              className="flex items-center gap-2 mt-2 px-4 py-2.5 text-sm font-black text-white rounded-2xl transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }}>
              <Plus size={14} /> New Enquiry
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400 font-medium">
            {filtered.length} enquir{filtered.length !== 1 ? 'ies' : 'y'}
            {filter !== 'all' && ` · ${STATUS_CFG[filter as TaskStatus]?.label}`}
            {search && ` matching "${search}"`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(task => (
              <EnquiryCard key={task.id} task={task}
                onClick={() => navigate(`/tasks/${task.id}`)} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default InquiryListPage
